import React, { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Form, Popper } from "@sscs/ui";
import { AccessTimeIcon as TimeIcon } from "@sscs/ui/icons";
import { DATE_FIELD_FORMAT, TIME_FIELD_FORMAT } from "@sscs/utils";
import { TimeRangeFormData } from "../../types";
import { TimeRangeFormSchema } from "../../utils";
import styles from "./dateTimeRangeFilter.module.scss";

type TimeRangeFilterFormProps = {
  startDate: Date;
  endDate: Date;
  onSave: (startDate: Date, endDate: Date) => void;
  onClose: () => void;
};

const TimeRangeFilterForm = ({
  startDate,
  endDate,
  onSave,
  onClose,
}: TimeRangeFilterFormProps): React.JSX.Element => {
  const formMethods = useForm<TimeRangeFormData>({
    resolver: yupResolver(TimeRangeFormSchema),
    mode: "onChange",
    defaultValues: {
      startDate: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
      endDate: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
    },
  });

  const {
    trigger,
    formState: { errors },
  } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <Form.Form
        onSubmit={formMethods.handleSubmit((data: TimeRangeFormData) => {
          onSave(new Date(data.startDate), new Date(data.endDate));
          onClose();
        })}
      >
        <Form.DateTimePicker
          name="startDate"
          label="From"
          step={1}
          className={styles["date-input"]}
          errors={errors}
          onChange={() => trigger("endDate")}
        />
        <Form.DateTimePicker
          name="endDate"
          label="To"
          step={1}
          className={styles["date-input"]}
          errors={errors}
        />
        <div className={styles["actions-container"]}>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!formMethods.formState.isDirty}>
            Apply time range
          </Button>
        </div>
      </Form.Form>
    </FormProvider>
  );
};

type DateTimeRangeFilterProps = {
  id: string;
  startDate: Date;
  endDate: Date;
  onSave: (startDate: Date, endDate: Date) => void;
};

const DateTimeRangeFilter = ({
  id,
  startDate,
  endDate,
  onSave,
}: DateTimeRangeFilterProps): React.JSX.Element => {
  const [popperIsOpen, setPopperIsOpen] = useState(false);

  const closeFilterForm = () => setPopperIsOpen(false);

  return (
    <Popper
      open={popperIsOpen}
      onClose={closeFilterForm}
      placement="right-start"
      className={styles.popper}
      trigger={(ref) => (
        <Button
          ref={ref}
          id={id}
          color="secondary"
          onClick={() => setPopperIsOpen(true)}
          startIcon={<TimeIcon />}
          className={styles["time-range-button"]}
        >
          {format(startDate, `${DATE_FIELD_FORMAT} ${TIME_FIELD_FORMAT}`)} to{" "}
          {format(endDate, `${DATE_FIELD_FORMAT} ${TIME_FIELD_FORMAT}`)}
        </Button>
      )}
      content={
        <TimeRangeFilterForm
          startDate={startDate}
          endDate={endDate}
          onSave={onSave}
          onClose={closeFilterForm}
        />
      }
    />
  );
};

export default DateTimeRangeFilter;
